
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slab } from "@/types/slab";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SlabEditFieldsProps {
  formData: Slab;
  setFormData: (data: Slab) => void;
  errors: Record<string, string>;
  materialTypes?: string[];
}

export function SlabEditFields({
  formData,
  setFormData,
  errors,
  materialTypes = []
}: SlabEditFieldsProps) {
  // Query para locations
  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cat_locations')
        .select('*')
        .eq('active', true);
      return data || [];
    }
  });

  // Query para price groups
  const { data: priceGroups } = useQuery({
    queryKey: ['priceGroups'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cat_price_groups')
        .select('*')
        .eq('active', true);
      return data || [];
    }
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="code">Código (Automatico)</Label>
        <Input id="code" value={formData.code} readOnly disabled className="bg-muted" />
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Nome</Label>
        <Input 
          id="name" 
          value={formData.name} 
          onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} 
          className={errors.name ? "border-red-500" : ""}
          required 
        />
        {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="type">Material</Label>
        <Select 
          value={formData.type || ''} 
          onValueChange={value => setFormData({
            ...formData,
            type: value
          })}
        >
          <SelectTrigger className={errors.type ? "border-red-500" : ""}>
            <SelectValue placeholder="Selecione o tipo" />
          </SelectTrigger>
          <SelectContent>
            {materialTypes.map(type => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.type && <p className="text-red-500 text-sm">{errors.type}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="priceGroup">Grupo de Preço</Label>
        <Select 
          value={formData.price_group_id} 
          onValueChange={value => setFormData({
            ...formData,
            price_group_id: value
          })}
        >
          <SelectTrigger className={errors.price_group ? "border-red-500" : ""}>
            <SelectValue placeholder="Selecione o grupo de preço" />
          </SelectTrigger>
          <SelectContent>
            {priceGroups?.map(group => (
              <SelectItem key={group.id} value={group.id}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.price_group && <p className="text-red-500 text-sm">{errors.price_group}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="location">Localização</Label>
        <Select 
          value={formData.location_id} 
          onValueChange={value => setFormData({
            ...formData,
            location_id: value
          })}
        >
          <SelectTrigger className={errors.location ? "border-red-500" : ""}>
            <SelectValue placeholder="Selecione a localização" />
          </SelectTrigger>
          <SelectContent>
            {locations?.map(loc => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.location && <p className="text-red-500 text-sm">{errors.location}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="price">Preço</Label>
        <Input 
          id="price" 
          type="number" 
          value={formData.price} 
          onChange={e => setFormData({
            ...formData,
            price: Number(e.target.value)
          })} 
          className={errors.price ? "border-red-500" : ""}
          min="0" 
          required 
        />
        {errors.price && <p className="text-red-500 text-sm">{errors.price}</p>}
      </div>
    </div>
  );
}
